// src/components/Admin/GameEditor.js
import React, { useState, useEffect, useCallback } from 'react';

export default function GameEditor({ token }) {
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [message, setMessage] = useState('');

  const fetchGames = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/games/all', {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        const gameArray = Array.isArray(data) ? data : (data.games || []);
        console.log('[ADMIN] Loaded games:', gameArray.length);
        setGames(gameArray);
      } else {
        console.error('[ADMIN] Failed to load games:', res.status);
        setGames([]);
      }
    } catch (err) {
      console.error('[ADMIN] Error loading games:', err);
      setGames([]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchGames();
  }, [fetchGames]);

  const emptyGame = {
    name: '',
    description: '',
    url: '',
    category: 'interactive',
    active: true,
  };

    async function saveGame(gameData) {
    try {
      const isNew = !gameData.id;
      const url = isNew ? '/api/games' : `/api/games/${gameData.id}`;
      const method = isNew ? 'POST' : 'PUT';

      // Remove id from payload — backend handles it
      const { id, ...payload } = gameData;

      console.log(`[ADMIN] ${method} ${url}`, payload);

      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        setEditing(null);
        setMessage(isNew ? 'Game created!' : 'Game updated!');
        setTimeout(() => setMessage(''), 3000);
        fetchGames();
      } else {
        const err = await res.json();
        setMessage(`Error: ${err.error || 'Failed to save'}`);
      }
    } catch (err) {
      console.error('[ADMIN] Save game error:', err);
      setMessage('Error connecting to server');
    }
  }

  async function deleteGame(id) {
    if (!window.confirm('Delete this game?')) return;

    try {
      const res = await fetch(`/api/games/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (res.ok) {
        setMessage('Game deleted');
        setTimeout(() => setMessage(''), 3000);
        fetchGames();
      } else {
        setMessage('Failed to delete game');
      }
    } catch (err) {
      setMessage('Error connecting to server');
    }
  }

  async function toggleActive(game) {
    await saveGame({ ...game, active: !game.active });
  }

  if (loading) {
    return (
      <div style={{ color: '#555', padding: '20px', textAlign: 'center' }}>
        Loading games...
      </div>
    );
  }

  if (editing) {
    return (
      <GameForm
        game={editing}
        onSave={saveGame}
        onCancel={() => setEditing(null)}
        message={message}
      />
    );
  }

  return (
    <div>
      {message && (
        <div style={{
          padding: '10px 15px',
          marginBottom: '15px',
          border: `1px solid ${message.includes('Error') ? '#ff0040' : '#00ff41'}`,
          color: message.includes('Error') ? '#ff0040' : '#00ff41',
        }}>
          {message}
        </div>
      )}

      <button
        className="bbs-button"
        onClick={() => setEditing({ ...emptyGame })}
        style={{ marginBottom: '20px' }}
      >
        + NEW GAME
      </button>

      {games.length === 0 ? (
        <div style={{ color: '#555', padding: '20px' }}>
          No games yet. Click "+ NEW GAME" to create one.
        </div>
      ) : (
        <div>
          {games.map(game => {
            if (!game || !game.id) return null;

            return (
              <div
                key={game.id}
                style={{
                  border: '1px solid #333',
                  padding: '12px 15px',
                  marginBottom: '8px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  opacity: game.active ? 1 : 0.5,
                }}
              >
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{ color: '#00e5ff', fontWeight: 'bold' }}>
                      {game.name || 'Untitled'}
                    </span>
                    <span style={{
                      color: game.active ? '#00ff41' : '#ff0040',
                      fontSize: '0.75rem',
                      padding: '2px 6px',
                      border: `1px solid ${game.active ? '#00ff41' : '#ff0040'}`,
                    }}>
                      {game.active ? 'ACTIVE' : 'HIDDEN'}
                    </span>
                    {game.category && (
                      <span style={{
                        color: '#ffb000',
                        fontSize: '0.7rem',
                        padding: '2px 6px',
                        border: '1px solid #ffb000',
                      }}>
                        {game.category.toUpperCase()}
                      </span>
                    )}
                  </div>
                  <div style={{ color: '#555', fontSize: '0.8rem', marginTop: '4px' }}>
                    {game.url || 'No URL set'}
                  </div>
                  {game.description && (
                    <div style={{ color: '#444', fontSize: '0.75rem', marginTop: '2px' }}>
                      {game.description.substring(0, 120)}
                      {game.description.length > 120 ? '...' : ''}
                    </div>
                  )}
                </div>

                <div style={{ display: 'flex', gap: '8px', marginLeft: '15px' }}>
                  <button
                    className="bbs-button"
                    onClick={() => toggleActive(game)}
                    style={{
                      padding: '4px 10px',
                      fontSize: '0.75rem',
                      borderColor: game.active ? '#ff0040' : '#00ff41',
                      color: game.active ? '#ff0040' : '#00ff41',
                    }}
                  >
                    {game.active ? 'HIDE' : 'SHOW'}
                  </button>
                  <button
                    className="bbs-button"
                    onClick={() => setEditing({ ...game })}
                    style={{ padding: '4px 10px', fontSize: '0.75rem' }}
                  >
                    EDIT
                  </button>
                  <button
                    className="bbs-button"
                    onClick={() => deleteGame(game.id)}
                    style={{
                      padding: '4px 10px',
                      fontSize: '0.75rem',
                      borderColor: '#ff0040',
                      color: '#ff0040',
                    }}
                  >
                    DEL
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function GameForm({ game, onSave, onCancel, message }) {
  const [form, setForm] = useState({
    id: (game && game.id) || null,
    name: (game && game.name) || '',
    description: (game && game.description) || '',
    url: (game && game.url) || '',
    category: (game && game.category) || 'interactive',
    active: game ? game.active !== false : true,
  });

  function handleChange(field, value) {
    setForm(prev => ({ ...prev, [field]: value }));
  }

  function handleSubmit(e) {
    e.preventDefault();
    if (!form.name.trim()) {
      alert('Game name is required');
      return;
    }
    onSave(form);
  }

  return (
    <form onSubmit={handleSubmit}>
      {message && (
        <div style={{
          padding: '10px 15px',
          marginBottom: '15px',
          border: `1px solid ${message.includes('Error') ? '#ff0040' : '#00ff41'}`,
          color: message.includes('Error') ? '#ff0040' : '#00ff41',
        }}>
          {message}
        </div>
      )}

      <h3 style={{ color: '#00e5ff', marginBottom: '20px' }}>
        {form.id ? 'EDIT GAME' : 'NEW GAME'}
      </h3>

      <div style={{ marginBottom: '15px' }}>
        <label style={{ color: '#ffb000', display: 'block', marginBottom: '5px', fontSize: '0.85rem' }}>
          NAME *
        </label>
        <input
          type="text"
          value={form.name}
          onChange={e => handleChange('name', e.target.value)}
          className="admin-input"
          placeholder="Game name"
          style={{ width: '100%' }}
        />
      </div>

      <div style={{ marginBottom: '15px' }}>
        <label style={{ color: '#ffb000', display: 'block', marginBottom: '5px', fontSize: '0.85rem' }}>
          URL / PATH
        </label>
        <input
          type="text"
          value={form.url}
          onChange={e => handleChange('url', e.target.value)}
          className="admin-input"
          placeholder="/games/my-game or https://..."
          style={{ width: '100%' }}
        />
      </div>

      <div style={{ marginBottom: '15px' }}>
        <label style={{ color: '#ffb000', display: 'block', marginBottom: '5px', fontSize: '0.85rem' }}>
          DESCRIPTION
        </label>
        <textarea
          value={form.description}
          onChange={e => handleChange('description', e.target.value)}
          className="admin-input"
          placeholder="What is this game about?"
          rows={4}
          style={{ width: '100%', resize: 'vertical' }}
        />
      </div>

      <div style={{ marginBottom: '15px' }}>
        <label style={{ color: '#ffb000', display: 'block', marginBottom: '5px', fontSize: '0.85rem' }}>
          CATEGORY
        </label>
        <select
          value={form.category}
          onChange={e => handleChange('category', e.target.value)}
          className="admin-input"
          style={{
            width: '100%',
            background: '#0a0a0a',
            color: '#00ff41',
            border: '1px solid #333',
            padding: '8px',
            fontFamily: "'VT323', 'Courier New', monospace",
          }}
        >
          <option value="interactive">Interactive Fiction</option>
          <option value="puzzle">Puzzle</option>
          <option value="simulation">Simulation</option>
          <option value="arcade">Arcade</option>
          <option value="other">Other</option>
        </select>
      </div>

      <div style={{ marginBottom: '20px' }}>
        <label style={{
          color: form.active ? '#00ff41' : '#ff0040',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
        }}>
          <input
            type="checkbox"
            checked={form.active}
            onChange={e => handleChange('active', e.target.checked)}
          />
          {form.active ? 'ACTIVE — Visible on public site' : 'HIDDEN — Not shown to visitors'}
        </label>
      </div>

      <div style={{ display: 'flex', gap: '10px' }}>
        <button type="submit" className="bbs-button">
          {form.id ? 'SAVE CHANGES' : 'CREATE GAME'}
        </button>
        <button
          type="button"
          className="bbs-button"
          onClick={onCancel}
          style={{ borderColor: '#555', color: '#555' }}
        >
          CANCEL
        </button>
      </div>
    </form>
  );
}